import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { Link, useNavigate } from 'react-router-dom';
import { restaurantAPI, deliveryAPI } from '../api/api';
import { useAuth } from '../App';
import { DeliveryStatusBadge } from '../components/DriverStatusBadge';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function PreferredDrivers() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const res = await restaurantAPI.getPreferredDrivers();
      setData(res.data);
    } catch (e) {
      setData({ preferredDrivers: [], cap: 0, count: 0, plan: null });
    }
  }

  useEffect(() => { load(); }, []);

  async function add() {
    if (!code.trim()) return;
    setBusy(true); setMsg(''); setErr('');
    try {
      const res = await restaurantAPI.addPreferredDriver(code.trim());
      setMsg(res.data.message || 'Request sent.');
      setCode('');
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not add that driver.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(driverId) {
    setMsg(''); setErr('');
    try {
      await restaurantAPI.removePreferredDriver(driverId);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not remove that driver.');
    }
  }

  if (!data) return null;

  if (data.cap === 0) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2"><span>⭐</span> Preferred Drivers</h3>
        <p className="text-gray-400 text-sm mb-4">
          Reserve drivers you trust — they get your deliveries first, automatically.
        </p>
        <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-4">
          <p className="text-yellow-400 text-sm font-medium">Upgrade to Business to add preferred drivers.</p>
          <Link to="/pricing" className="inline-block mt-3 px-4 py-2 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition text-sm">
            View plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2"><span>⭐</span> Preferred Drivers</h3>
        <span className="text-xs text-gray-500">{data.count} of {data.cap}</span>
      </div>
      <p className="text-gray-400 text-sm mb-4">
        Drivers you reserve get your deliveries first. Add a driver using the code they share with you.
      </p>

      {data.count < data.cap && (
        <div className="flex gap-2 mb-4">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="DRIVER CODE (e.g. SHARIFBEY-4827)"
            className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-400"
          />
          <button onClick={add} disabled={busy}
            className="px-5 py-2.5 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition text-sm disabled:opacity-50">
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}

      {msg && <p className="text-green-400 text-xs mb-3">{msg}</p>}
      {err && <p className="text-red-400 text-xs mb-3">{err}</p>}

      {data.preferredDrivers.length === 0 ? (
        <p className="text-gray-500 text-sm">No preferred drivers yet.</p>
      ) : (
        <div className="space-y-2">
          {data.preferredDrivers.map((p) => (
            <div key={p.driver?._id || p.driver} className="flex items-center justify-between bg-gray-800/50 rounded-xl px-4 py-3">
              <div>
                <span className="text-white text-sm font-medium">{p.driver?.name || 'Driver'}</span>
                {p.status === 'active' ? (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">Active</span>
                ) : (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-400">Awaiting driver</span>
                )}
              </div>
              <button onClick={() => remove(p.driver?._id || p.driver)} className="text-gray-500 hover:text-red-400 transition text-sm">
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RestaurantDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [stats, setStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  async function load() {
    try {
      const [rRes, sRes, dRes] = await Promise.all([
        restaurantAPI.getMe(),
        restaurantAPI.getStats(),
        deliveryAPI.getAll({ limit: 10 }),
      ]);
      setRestaurant(rRes.data);
      setStats(sRes.data);
      setDeliveries(dRes.data.deliveries || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!restaurant?._id) return;
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('connect', () => {
      socket.emit('join:restaurant', { restaurantId: restaurant._id });
    });
    socket.on('job:statusUpdate', () => { load(); });
    return () => { socket.disconnect(); };
  }, [restaurant?._id]);

  const planBadge = {
    starter: 'bg-gray-700 text-gray-300',
    business: 'bg-yellow-400/20 text-yellow-400',
    enterprise: 'bg-green-400/20 text-green-400',
    null: 'bg-gray-700 text-gray-400',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1
            className="text-2xl font-black text-yellow-400"
            style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}
          >
            STARFUEL
          </h1>
          <span className="hidden md:block text-gray-600 text-sm">|</span>
          <span className="hidden md:block text-gray-400 text-sm">{restaurant?.name}</span>
        </div>
        <nav className="flex items-center gap-2 md:gap-4">
          <Link
            to="/create-delivery"
            className="px-4 py-2 bg-yellow-400 text-black text-sm font-bold rounded-xl hover:bg-yellow-300 transition"
          >
            + New Delivery
          </Link>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="px-3 py-2 text-gray-400 hover:text-white text-sm transition"
          >
            Sign Out
          </button>
        </nav>
      </header>

      <main className="px-4 md:px-8 py-8 max-w-6xl mx-auto">
        {/* Welcome + plan */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold">
              Good to see you, {user?.profile?.firstName} 👋
            </h2>
            <p className="text-gray-400 mt-1">{restaurant?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${planBadge[restaurant?.activePlan] || planBadge.null}`}
            >
              {restaurant?.activePlan || 'No Plan'} plan
            </span>
            {restaurant?.deliveriesRemaining > 0 && (
              <span className="text-sm text-gray-400">
                {restaurant.deliveriesRemaining} deliveries left
              </span>
            )}
            <Link to="/billing" className="text-yellow-400 text-sm hover:underline">
              Manage Plan →
            </Link>
          </div>
        </div>

        {/* Stat cards */}
        {!restaurant?.paymentSetup && (
          <div className="mb-6 flex items-center justify-between p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-2xl">
            <div className="flex items-center gap-3">
              <span className="text-xl">💳</span>
              <div>
                <p className="text-yellow-400 font-semibold text-sm">Add a payment method</p>
                <p className="text-gray-400 text-xs">Required to pay for deliveries</p>
              </div>
            </div>
            <Link to="/billing" className="px-4 py-2 bg-yellow-400 text-black text-sm font-bold rounded-xl hover:bg-yellow-300 transition flex-shrink-0">Set Up →</Link>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Deliveries', value: stats?.total || 0, icon: '📦' },
            { label: 'Completed', value: stats?.byStatus?.delivered?.count || 0, icon: '✅' },
            { label: 'Active', value: (stats?.byStatus?.assigned?.count || 0) + (stats?.byStatus?.accepted?.count || 0) + (stats?.byStatus?.picked_up?.count || 0), icon: '🔄' },
            { label: 'Total Spent', value: `$${Object.values(stats?.byStatus || {}).reduce((a, s) => a + (s.fees || 0), 0).toFixed(0)}`, icon: '💰' },
          ].map((stat) => (
            <div key={stat.label} className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'New Delivery', desc: 'Create a delivery job now', icon: '📍', path: '/create-delivery' },
            { label: 'Manage Billing', desc: 'View plans and invoices', icon: '💳', path: '/billing' },
          ].map((action) => (
            <Link
              key={action.label}
              to={action.path}
              className="bg-gray-900 border border-gray-800 hover:border-yellow-400/50 rounded-2xl p-6 transition-all group"
            >
              <div className="text-3xl mb-3">{action.icon}</div>
              <div className="font-semibold text-white group-hover:text-yellow-400 transition">{action.label}</div>
              <div className="text-sm text-gray-400 mt-1">{action.desc}</div>
            </Link>
          ))}
        </div>

        {/* Recent deliveries */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Recent Deliveries</h3>
            <Link to="/create-delivery" className="text-yellow-400 text-sm hover:underline">View all</Link>
          </div>

          {deliveries.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
              <div className="text-4xl mb-3">📦</div>
              <p className="text-gray-400">No deliveries yet</p>
              <Link
                to="/create-delivery"
                className="inline-block mt-4 px-6 py-2.5 bg-yellow-400 text-black font-semibold rounded-xl hover:bg-yellow-300 transition text-sm"
              >
                Create your first delivery
              </Link>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
              {deliveries.map((d, i) => (
                <Link
                  key={d._id}
                  to={`/delivery/${d._id}`}
                  className={`flex flex-col md:flex-row md:items-center justify-between p-4 gap-3 hover:bg-gray-800/50 transition ${i > 0 ? 'border-t border-gray-800' : ''}`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-white truncate max-w-xs">
                        {d.dropoff?.address}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>From: {d.pickup?.address}</span>
                      <span>•</span>
                      <span>{new Date(d.createdAt).toLocaleDateString()}</span>
                      {d.driver && <span>• Driver assigned</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-yellow-400 font-medium">${d.fee}</span>
                    <DeliveryStatusBadge status={d.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <PreferredDrivers />
      </main>
    </div>
  );
}
